/**
 * Shared OEM (Original Equipment Manufacturer) constants
 *
 * Used across:
 * - Zone Setup (ZoneMapTab, ZoneSetupTab)
 * - Tenant Setup (TenantSetupTab)
 * - Any future OEM-related features
 */

export const KNOWN_OEMS = [
  'Ford',
  'Stellantis',
  'General Motors',
  'Toyota',
  'Honda',
  'Nissan',
  'Hyundai',
  'Kia',
  'Volkswagen',
  'BMW',
  'Mercedes-Benz',
  'Audi',
  'Volvo',
  'Subaru',
  'Mazda',
  'Tesla',
  'Rivian',
  'Lucid',
  'Other'
] as const;

export type OEM = (typeof KNOWN_OEMS)[number];

/**
 * Default OEM for new zones
 * Most zones are currently Ford, so this provides a sensible default
 */
export const DEFAULT_OEM: OEM = 'Ford';

/**
 * Mapping of OEMs to their vehicle brand names
 * Used for filtering dealer locations by OEM on the Zone Map
 *
 * Note: Brand names should match the `make_name` values in the database
 * (case-insensitive comparison is used)
 */
export const OEM_BRANDS: Partial<Record<OEM, string[]>> = {
  Ford: ['Ford', 'Lincoln'],
  Stellantis: ['Chrysler', 'Dodge', 'Jeep', 'RAM', 'Ram'],
  'General Motors': ['Chevrolet', 'Buick', 'Cadillac', 'GMC'],
  Toyota: ['Toyota', 'Lexus'],
  Honda: ['Honda', 'Acura'],
  Nissan: ['Nissan', 'Infiniti'],
  Hyundai: ['Hyundai', 'Genesis'],
  Kia: ['Kia'],
  Volkswagen: ['Volkswagen', 'VW'],
  BMW: ['BMW', 'Mini'],
  'Mercedes-Benz': ['Mercedes-Benz', 'Mercedes'],
  Audi: ['Audi'],
  Volvo: ['Volvo'],
  Subaru: ['Subaru'],
  Mazda: ['Mazda'],
  Tesla: ['Tesla'],
  Rivian: ['Rivian'],
  Lucid: ['Lucid']
};

/**
 * Get the list of brand names for an OEM
 * Returns empty array if OEM has no brand mapping (shows all dealers)
 */
export function getBrandsForOEM(oem: string): string[] {
  return OEM_BRANDS[oem as OEM] || [];
}
